import { Link } from "react-router-dom";
import { MagneticButton } from "@/components/ui/magnetic-button";
import TextReveal from "@/components/TextReveal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useState, useRef, Suspense } from "react";
import { lazyWithReload } from "@/lib/lazyWithReload";

// Lazy load decorative components to improve FCP (with stale-deploy recovery)
const MusicInstruments = lazyWithReload(() => import("./MusicInstruments")) as any;
const MusicStaffLines = lazyWithReload(() => import("./MusicStaffLines")) as any;
const AudioEqualizer = lazyWithReload(() => import("./AudioEqualizer")) as any;

// Use WebP format for better compression (~80% smaller than JPG)
const heroImage = "/hero-jazz.webp";

const Hero = () => {
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  const sectionRef = useRef<HTMLElement>(null);
  const ticking = useRef(false);

  // Detect mobile/tablet for performance optimization
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile, { passive: true });
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Only track scroll on desktop for parallax effects
  useEffect(() => {
    if (isMobile) {
      setScrollY(0);
      return;
    }

    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile]);

  // Parallax values - disabled on mobile for performance
  const bgParallax = isMobile ? 0 : scrollY * 0.3;
  const bgScale = isMobile ? 1.05 : 1.05 + (scrollY * 0.0001);
  const contentParallax = isMobile ? 0 : scrollY * 0.12;
  const decorParallax1 = scrollY * 0.18;
  const decorParallax2 = scrollY * 0.22;
  const fadeOut = isMobile ? 1 : Math.max(0, 1 - scrollY / 700);


  return (
    <section ref={sectionRef} className="relative min-h-screen w-full flex items-center justify-center overflow-hidden pt-20 md:pt-0">
      {/* Lazy-loaded decorative components */}
      <Suspense fallback={null}>
        <MusicInstruments variant="hero" opacity={0.04} />
        <MusicStaffLines variant="wave" opacity={0.015} speed="slow" direction="right" />
      </Suspense>
      <div
        className="absolute inset-0 overflow-hidden"
      >
        {/* Hero image with smooth parallax transform */}
        <div 
          className="absolute inset-0 will-change-transform"
          style={{ 
            transform: `translate3d(0, ${bgParallax}px, 0) scale(${isLoaded ? bgScale : 1.15})`,
            transition: isLoaded ? 'transform 0.1s linear' : 'transform 1.5s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <img
            src={heroImage}
            alt="Jazz Insights Plovdiv - Live jazz performance venue"
            width={1920}
            height={1280}
            // @ts-expect-error - fetchpriority is valid HTML but React types don't include it yet
            fetchpriority="high"
            decoding="async"
            className="w-full h-[120%] object-cover object-center"
            style={{
              transform: 'translateY(-10%)',
            }}
          />
        </div>
        
        {/* Multi-layer gradient overlay for depth - stronger at bottom to hide watermarks */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/40 via-transparent to-background/40" />
        {/* Extra bottom overlay to ensure watermarks are hidden on all devices */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background via-background/95 to-transparent" />
        
        {/* Radial gold glow with parallax */}
        <div 
          className="absolute inset-0 opacity-30 will-change-transform"
          style={{
            background: 'radial-gradient(ellipse at 50% 60%, hsl(42 80% 55% / 0.15), transparent 60%)',
            transform: `translate3d(0, ${scrollY * 0.08}px, 0)`,
          }}
        />
      </div>

      {/* Art Deco Decorative Elements with parallax */}
      <div 
        className={`absolute top-20 left-10 w-40 h-40 border border-gold/20 rotate-45 hidden lg:block will-change-transform transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          transform: `rotate(45deg) translateY(${decorParallax1}px)`,
          opacity: fadeOut * 0.2,
        }}
      />
      <div 
        className={`absolute top-24 left-14 w-32 h-32 border border-gold/10 rotate-45 hidden lg:block will-change-transform transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          transform: `rotate(45deg) translateY(${decorParallax2}px)`,
          opacity: fadeOut * 0.15,
        }}
      />
      
      <div 
        className={`absolute bottom-32 right-10 w-28 h-28 border border-deep-red/30 hidden lg:block will-change-transform transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          transform: `rotate(12deg) translateY(${-decorParallax1}px)`,
          opacity: fadeOut * 0.3,
        }}
      />
      <div 
        className={`absolute bottom-36 right-14 w-20 h-20 border border-deep-red/15 hidden lg:block will-change-transform transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          transform: `rotate(12deg) translateY(${-decorParallax2}px)`,
          opacity: fadeOut * 0.2,
        }}
      />
      
      <div 
        className={`absolute top-40 right-20 w-20 h-20 bg-deep-red/5 hidden lg:block will-change-transform transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          transform: `rotate(12deg) translateY(${decorParallax1 * 0.8}px) scale(${1 + scrollY * 0.0003})`,
          opacity: fadeOut * 0.1,
        }}
      />


      {/* Audio Equalizer - Left Side */}
      <Suspense fallback={null}>
        <div 
          className={`absolute bottom-28 left-8 lg:left-24 hidden lg:flex transition-all duration-1000 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ 
            opacity: fadeOut * 0.6,
            transitionDelay: '800ms',
          }}
        >
          <AudioEqualizer barCount={6} className="h-16" />
        </div>

        {/* Audio Equalizer - Right Side */}
        <div 
          className={`absolute bottom-28 right-8 lg:right-24 hidden lg:flex transition-all duration-1000 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ 
            opacity: fadeOut * 0.6,
            transitionDelay: '900ms',
          }}
        >
          <AudioEqualizer barCount={6} className="h-16" />
        </div>
      </Suspense>

      {/* Decorative lines with parallax */}
      <div 
        className={`absolute top-1/4 left-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent hidden lg:block will-change-transform transition-all duration-1000 ${
          isLoaded ? 'opacity-100 w-32' : 'opacity-0 w-0'
        }`}
        style={{ 
          transform: `translateY(${decorParallax1 * 0.5}px)`,
          opacity: fadeOut * 0.3,
        }}
      />
      <div 
        className={`absolute bottom-1/3 right-0 h-px bg-gradient-to-l from-transparent via-gold/30 to-transparent hidden lg:block will-change-transform transition-all duration-1000 ${
          isLoaded ? 'opacity-100 w-32' : 'opacity-0 w-0'
        }`}
        style={{ 
          transform: `translateY(${-decorParallax1 * 0.5}px)`,
          opacity: fadeOut * 0.3,
        }}
      />

      {/* Floating particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden lg:block">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-gold/30 rounded-full"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + i * 10}%`,
              transform: `translateY(${scrollY * (0.1 + i * 0.05)}px)`,
              opacity: fadeOut * (0.3 + i * 0.1),
              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      {/* Content with subtle parallax */}
      <div 
        className="relative z-10 text-center px-6 max-w-4xl mx-auto will-change-transform"
        style={{ 
          transform: `translateY(${contentParallax}px)`,
          opacity: fadeOut,
        }}
      >
        {/* Established badge */}
        <div 
          className={`mb-8 transition-all duration-700 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          <span className="label-elegant">
            <TextReveal delay={0.3} staggerDelay={0.04} animateBy="letter">
              {t.hero.established}
            </TextReveal>
          </span>
        </div>

        {/* Main title */}
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold mb-4 md:mb-6 tracking-tight">
          <TextReveal 
            className="text-foreground drop-shadow-lg" 
            delay={0.5} 
            staggerDelay={0.08}
            animateBy="word"
          >
            {t.hero.title}
          </TextReveal>
        </h1>

        {/* Decorative line under title */}
        <div 
          className={`flex items-center justify-center gap-4 mb-8 transition-all duration-700 ${
            isLoaded ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
          }`}
          style={{ transitionDelay: '800ms' }}
        >
          <div className="w-16 h-px bg-gradient-to-r from-transparent to-gold/50" />
          <div className="w-2 h-2 rotate-45 border border-gold/50" />
          <div className="w-16 h-px bg-gradient-to-l from-transparent to-gold/50" />
        </div>

        {/* Subtitle */}
        <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground font-body font-light mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed px-2">
          <TextReveal delay={0.9} staggerDelay={0.025} animateBy="word">
            {t.hero.subtitle}
          </TextReveal>
        </p>

        {/* CTA Buttons */}
        <div 
          className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all duration-700 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: '900ms' }}
        >
          <MagneticButton variant="hero" size="xl" asChild className="group" strength={0.4} radius={150}>
            <Link to="/tickets" className="relative overflow-hidden">
              <span className="relative z-10">{t.nav.buyTickets}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cream/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </Link>
          </MagneticButton>
          <MagneticButton variant="outline" size="xl" asChild className="group" strength={0.35} radius={120}>
            <a href="#shows" className="relative overflow-hidden">
              <span className="relative z-10">{t.hero.viewShows}</span>
            </a>
          </MagneticButton>
        </div>
      </div>

      {/* Scroll Indicator - hidden on mobile since vinyl is there */}
      <div 
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-3 transition-all duration-700 ${
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ 
          transitionDelay: '1200ms',
          opacity: fadeOut * 0.8,
        }}
      >
        <span className="text-xs uppercase tracking-[0.2em] text-gold/60 font-body animate-pulse">
          Scroll
        </span>
        <div className="relative w-6 h-10 border-2 border-gold/40 rounded-full flex justify-center p-1 shadow-[0_0_15px_hsl(var(--gold)/0.2)] animate-[glow_2s_ease-in-out_infinite_alternate]">
          <div className="w-1.5 h-3 bg-gold rounded-full animate-[scrollBounce_1.5s_ease-in-out_infinite]" />
        </div>
        <div className="flex gap-1">
          <div className="w-1 h-1 bg-gold/40 rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" style={{ animationDelay: '0ms' }} />
          <div className="w-1 h-1 bg-gold/40 rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" style={{ animationDelay: '200ms' }} />
          <div className="w-1 h-1 bg-gold/40 rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" style={{ animationDelay: '400ms' }} />
        </div>
      </div>
    </section>
  );
};

export default Hero;